import axios from "axios";
import axiosRetry from "axios-retry";
import { ValuePartExchangeFormState } from "features/vehicleDetails/modals/ValuePartExchangeForm";
import axiosOnSuccessEvent from "utils/axiosOnSuccessEvent";
import { logError } from "utils/logger";

export declare module PartExchangeQuestions {
    export interface TooUnique {
        Id: "noValuationErrorMessage";
        Message: string;
    }

    export interface Questions {
        RegistrationNumberDataEntryQuestionViewModel: RegistrationNumberDataEntryQuestionViewModel;
        MileageDataEntryQuestionViewModel: MileageDataEntryQuestionViewModel;
        PartExchangeQuestionViewModels: PartExchangeQuestionViewModel[];
        CapValuation: CapValuation;
        ValuationViewModel: any;
        PurchaseJourneyValuationViewModel: any;
        IsReadOnly: boolean;
        ShowPersonalDetails: boolean;
        ShowSaveAndContinue: boolean;
        ContactPhoneNumber: string;
        IsEligibleForRetail: boolean;
        CorrelationId: any;
        PartExchangeValuationCustomerDetailsViewModel: PartExchangeValuationCustomerDetailsViewModel;
        ErrorMessage: any;
        ErrorMessageId: any;
        HasVehicle: boolean;
        FindVehicleButtonText: string;
    }

    export interface RegistrationNumberDataEntryQuestionViewModel {
        AttributesDictionary: AttributesDictionary;
        ValidationRegex: string;
        DisplayFullWidth: boolean;
        RenderLabel: boolean;
        UseUppercaseText: boolean;
        Identifier: string;
        Title: string;
        Answer: string;
        Type: string;
    }

    export interface AttributesDictionary {
        id: string;
        Name: string;
        "data-id": string;
        "data-validation": string;
        "data-validation-message": string;
        placeholder: string;
        required: string;
        class: string;
        "data-cs-mask": string;
        disabled: string;
    }

    export interface MileageDataEntryQuestionViewModel {
        AttributesDictionary: AttributesDictionary2;
        ValidationRegex: string;
        DisplayFullWidth: boolean;
        RenderLabel: boolean;
        UseUppercaseText: boolean;
        Identifier: string;
        Title: string;
        Answer: string;
        Type: string;
    }

    export interface AttributesDictionary2 {
        id: string;
        Name: string;
        "data-id": string;
        "data-validation": string;
        "data-validation-message": string;
        placeholder: string;
        required: string;
        class: string;
        "data-cs-mask": string;
        disabled: string;
    }

    export interface PartExchangeQuestionViewModel {
        Responses?: Response[];
        Selected: any;
        Identifier: string;
        Title: string;
        Answer: any;
        Type: string;
        FirstResponse?: FirstResponse;
        SecondResponse?: SecondResponse;
        AttributesDictionary?: AttributesDictionary3;
        ValidationRegex?: string;
        DisplayFullWidth?: boolean;
        RenderLabel?: boolean;
        UseUppercaseText?: boolean;
    }

    export interface Response {
        Identifier: string;
        Title: string;
        Description: string;
    }

    export interface FirstResponse {
        Identifier: string;
        Title: string;
        Description: string;
    }

    export interface SecondResponse {
        Identifier: string;
        Title: string;
        Description: string;
    }

    export interface AttributesDictionary3 {
        id: string;
        Name: string;
        "data-id": string;
        "data-validation": string;
        "data-validation-message": string;
        placeholder: string;
        required: string;
        class: string;
        "data-cs-mask": string;
    }

    export interface CapValuation {
        VRM: string;
        Mileage: number;
        Make: string;
        Range: string;
        Trim: string;
        BodyStyle: string;
        FuelType: string;
        FirstRegisteredDate: string;
        Colour: string;
        Transmission: string;
        EngineSize: number;
        BuildYear: number;
        ValuationImageUri: string;
        NeedToMakeAChangeContent: NeedToMakeAchangeContent;
    }

    export interface NeedToMakeAchangeContent {}

    export interface PartExchangeValuationCustomerDetailsViewModel {
        FirstName: any;
        LastName: any;
        EmailAddress: any;
        MobileNumber: any;
        PostCode: any;
        WouldLikeToRecieveNewsAndOffers: boolean;
        UserIsLoggedIn: boolean;
    }
}

export declare module PartExchangeQuestionAnswers {
    export interface Answers {
        UserAnswers: UserAnswers;
        PersonalDetailsModel: PersonalDetailsModel;
        RecaptchaToken: string;
    }

    export interface UserAnswers {
        registration_number: string;
        mileage: string;
        vehicle_condition: string;
        service_history: string;
        spare_key: string;
        is_private_plate: string;
        will_reg_be_on_vehicle: string;
        ownership_and_usage: string;
        outstanding_balance: string;
        reason_for_valuation?: string;
    }

    export interface PersonalDetailsModel {
        FirstName: string;
        LastName: string;
        EmailAddress: string;
        MobileNumber: string;
        Postcode: string;
        MarketingSignUp: string;
    }
}

export declare module PartExchangeQuestionAnswersResponse {
    export interface AnswersResponse {
        Valuation: number;
        ValuationRange: ValuationRange;
        VehicleRegistration: string;
        Mileage: number;
        Make: string;
        Model: string;
        Trim: string;
        Colour: string;
        Equity: number;
        PreliminaryAppraisalId: string;
        ExpiryDate: string;
        ValuationImageUri: string;
    }

    export interface ValuationRange {
        MaximumValuation: number;
        MaximumValuationPercentage: number;
        MinimumValuation: number;
        MinimumValuationPercentage: number;
    }
}

export type PartExchangeQuestionsError = { message: string };

export const getPartExchangeQuestions = async ({
    mileage,
    registration_number,
}: ValuePartExchangeFormState): Promise<
    PartExchangeQuestions.Questions | PartExchangeQuestions.TooUnique | undefined
> => {
    try {
        axiosRetry(axios, { retries: 3, retryDelay: axiosRetry.exponentialDelay });

        const response = await axios.get<PartExchangeQuestions.Questions>(
            `${
                process.env.NEXT_PUBLIC_PART_EXCHANGE_QUESTIONS_ENDPOINT
            }/${registration_number.toLowerCase()}/${mileage}`,
            {
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );

        return response.data;
    } catch (error) {
        const e = error as PartExchangeQuestionsError;
        logError(e.message, error);
    }
};

export const postPartExchangeQuestionAnswers = async (
    answers: PartExchangeQuestionAnswers.Answers
): Promise<
    | PartExchangeQuestionAnswersResponse.AnswersResponse
    | PartExchangeQuestions.TooUnique
    | undefined
> => {
    try {
        const response = await axios.post<
            PartExchangeQuestionAnswersResponse.AnswersResponse | PartExchangeQuestions.TooUnique
        >(`${process.env.NEXT_PUBLIC_PART_EXCHANGE_ANSWERS_ENDPOINT}`, answers, {
            headers: {
                "Content-Type": "application/json",
                Accept: "/",
                "Cache-Control": "no-cache",
            },
            withCredentials: true,
        });

        axiosOnSuccessEvent(response);

        return response.data;
    } catch (error) {
        const e = error as PartExchangeQuestionsError;
        logError(e.message, error);
        return {
            Id: "noValuationErrorMessage",
            Message: "We are unable to provide a valuation at this time.",
        };
    }
};
